import { arrayMove } from '@dnd-kit/sortable';
import { type Transform } from '@dnd-kit/utilities';
import { getIn, setIn, type FormikValues } from 'formik';

import { FormBlock, FormField } from 'types';

export const BLOCK_PREFIX = 'block:';
export const FIELD_PREFIX = 'field:';

export const getBlockItemId = (id: string) => `${BLOCK_PREFIX}${id}`;

export const getFieldItemId = (id: string) => `${FIELD_PREFIX}${id}`;

export const getEntityIdFromItemId = (itemId: string) => {
  if (itemId.startsWith(BLOCK_PREFIX)) return itemId.slice(BLOCK_PREFIX.length);
  if (itemId.startsWith(FIELD_PREFIX)) return itemId.slice(FIELD_PREFIX.length);
  return itemId;
};

export const getNonScalingTransform = (transform: Transform | null) =>
  transform ? { ...transform, scaleX: 1, scaleY: 1 } : null;

export const isFieldItemId = (itemId: string) =>
  itemId.startsWith(FIELD_PREFIX);

export const isBlockItemId = (itemId: string) =>
  itemId.startsWith(BLOCK_PREFIX);

export const getBlockSortableIds = (blocks: FormBlock[]) =>
  blocks.map((block) => getBlockItemId(block.id));

export const getFieldSortableIds = (fields: FormField[]) =>
  fields.map((field) => getFieldItemId(field.id));

export const reorderBlocks = (
  values: FormikValues,
  blocksPath: string,
  activeItemId: string,
  overItemId: string
) => {
  const blocks = (getIn(values, blocksPath) ?? []) as FormBlock[];
  const activeId = getEntityIdFromItemId(activeItemId);
  const overId = getEntityIdFromItemId(overItemId);
  const oldIndex = blocks.findIndex((block) => block.id === activeId);
  const newIndex = blocks.findIndex((block) => block.id === overId);

  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return values;
  }

  return setIn(values, blocksPath, arrayMove(blocks, oldIndex, newIndex));
};

const getTargetIndex = (
  targetFields: FormField[],
  overItemId?: string | null
) => {
  if (!overItemId || !isFieldItemId(overItemId)) {
    return targetFields.length;
  }

  const overId = getEntityIdFromItemId(overItemId);
  const targetIndex = targetFields.findIndex((field) => field.id === overId);

  return targetIndex < 0 ? targetFields.length : targetIndex;
};

export const moveFieldBetweenContainers = ({
  values,
  overItemId,
  sourcePath,
  targetPath,
  activeItemId,
}: {
  sourcePath: string;
  targetPath: string;
  values: FormikValues;
  activeItemId: string;
  overItemId?: string | null;
}) => {
  const activeId = getEntityIdFromItemId(activeItemId);
  const sourceFields = (getIn(values, sourcePath) ?? []) as FormField[];
  const sourceIndex = sourceFields.findIndex((field) => field.id === activeId);

  if (sourceIndex < 0) {
    return values;
  }

  if (sourcePath === targetPath) {
    const targetIndex = getTargetIndex(sourceFields, overItemId);

    if (targetIndex === sourceIndex) {
      return values;
    }

    return setIn(
      values,
      sourcePath,
      arrayMove(sourceFields, sourceIndex, targetIndex)
    );
  }

  const targetFields = (getIn(values, targetPath) ?? []) as FormField[];
  const nextSourceFields = [...sourceFields];
  const [movedField] = nextSourceFields.splice(sourceIndex, 1);

  if (!movedField) {
    return values;
  }

  const insertionIndex = getTargetIndex(targetFields, overItemId);
  const nextTargetFields = [...targetFields];

  nextTargetFields.splice(insertionIndex, 0, movedField);

  return setIn(
    setIn(values, sourcePath, nextSourceFields),
    targetPath,
    nextTargetFields
  );
};
